import {
  Box,
  Typography,
  Button,
  TextField,
  Tooltip,
  FormControl,
  MenuItem,
  Select,
  Divider,
  Switch,
  Paper,
  PaperProps,
} from "@mui/material";
import React, {
  FC,
  ReactElement,
  useContext,
  useState,
  useEffect,
} from "react";
import { MeetingItemRepresentation } from "../models/meetingItemRepresentation";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import ItemStatus from "./itemStatus";
import dateFormatter, { dateTimeFormatter } from "../util/dateFormatter";
import SendIcon from "@mui/icons-material/Send";
import { AppContext } from "../models/applicationState";
import { Context } from "./context";
import { useMsal } from "@azure/msal-react";
import {
  Meeting,
  MeetingItem,
  RiskIndicator,
  enumFromStringValue,
} from "../models";
import { FreeSingleSelect } from "./freeSingleSelect";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import ConfirmationPanel from "./confirmationPanel";
import Draggable from "react-draggable";
import { AttachmentsModal } from "./attachmentsModal";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EventNoteIcon from "@mui/icons-material/EventNote";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { MeetingService } from "../services/listService";
import config from "../config";
import { styled } from "@mui/material/styles";
import { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import PersonalNotesModal from "./meetingNotes/PersonalNotesModal";
import { PersonalNote, PersonalNoteTargetType } from "../models/personalNote";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";

const MeetingInfoTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[8],
    borderRadius: 8,
    padding: 0,
    maxWidth: 360,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

interface ItemEditingPanelProps {
  meetingItem: MeetingItemRepresentation;
  open: boolean;
  meeting?: Meeting;
  allowMetadataEditing?: boolean;
  allowAttachmentsEditing?: boolean;
  readOnly?: boolean;
  onClose: () => Promise<void> | void;
  onUpdate: (item: MeetingItemRepresentation) => Promise<void> | void;
  onAttachmentsCountChanged?: (
    count: number,
    meetingId?: string,
    itemId?: string,
  ) => Promise<void> | void;
}

function PaperComponent(props: PaperProps) {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  return (
    <Draggable
      nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

const systemCommentMarkers = (t: TFunction<"translation", undefined>) => [
  t("item.status_changed"),
  t("meeting_status.published"),
  t("item.dueDate_changed"),
  t("item.risk_changed"),
  t("item.content_changed"),
  t("item.prioritization_changed"),
  t("item.actor_changed"),
];

const isSystemCommentLine = (
  line: string,
  t: TFunction<"translation", undefined>,
) => {
  const markers = systemCommentMarkers(t)
    .map((m) => m?.toLowerCase())
    .filter(Boolean);
  const text = line.replace(/^\[.*?\]:\s*/, "").toLowerCase();
  return markers.some(
    (marker) => text === marker || text.startsWith(`${marker}:`),
  );
};

const splitCommentLines = (comments?: string) =>
  (comments || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

export const hasUserComments = (
  item: MeetingItemRepresentation,
  t: TFunction<"translation", undefined>,
) => {
  if (!item?.comments) {
    return false;
  }
  return splitCommentLines(item.comments).some(
    (line) => !isSystemCommentLine(line, t),
  );
};
const addCommentToItem = (
  item: MeetingItemRepresentation,
  userFullName: string,
  comment: string,
  t: TFunction<"translation", undefined>,
) => {
  addCommentToItemInternal(item, userFullName, comment);
};

export const addStatusUpdateSystemCommentToItem = (
  item: MeetingItemRepresentation,
  userFullName: string,
  t: TFunction<"translation", undefined>,
) => {
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.status_changed") + ": " + t(`item_status.${item.status}`),
  );
};

export const addPublishSystemCommentToItem = (
  item: MeetingItemRepresentation | MeetingItem,
  userFullName: string,
  t: TFunction<"translation", undefined>,
) => {
  addCommentToItemInternal(item, userFullName, t("meeting_status.published"));
};

export const addDueDateChangeCommentToItem = (
  item: MeetingItemRepresentation,
  userFullName: string,
  newDueDate: Date | undefined,
  t: TFunction<"translation", undefined>,
) => {
  const parsedDate = newDueDate ? new Date(newDueDate as any) : undefined;
  const newDateString =
    parsedDate && !isNaN(parsedDate.getTime())
      ? dateFormatter.format(parsedDate)
      : t("item.due_date.not_set");
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.dueDate_changed") + ": " + newDateString,
  );
};

export const addRiskChangeCommentToItem = (
  item: MeetingItemRepresentation,
  userFullName: string,
  newRisk: RiskIndicator,
  t: TFunction<"translation", undefined>,
) => {
  const newRiskString = t(`item_risk.${newRisk}`);
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.risk_changed") + ": " + newRiskString,
  );
};

export const addContentChangeCommentToItem = (
  item: MeetingItemRepresentation,
  userFullName: string,
  t: TFunction<"translation", undefined>,
) => {
  addCommentToItemInternal(item, userFullName, t("item.content_changed"));
};

export const addPrioritizationChangeCommentToItem = (
  item: MeetingItemRepresentation,
  userFullName: string,
  newPrioritized: boolean,
  t: TFunction<"translation", undefined>,
) => {
  const newPrioritizedString = newPrioritized
    ? t("item_priority.high_priority")
    : t("item_priority.normal_priority");
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.prioritization_changed") + ": " + newPrioritizedString,
  );
};

export const addActorChangeCommentToItem = (
  item: MeetingItemRepresentation,
  userFullName: string,
  newActor: string,
  t: TFunction<"translation", undefined>,
) => {
  const newActorString = newActor || t("item.actor_not_set");
  addCommentToItemInternal(
    item,
    userFullName,
    t("item.actor_changed") + ": " + newActorString,
  );
};

const addCommentToItemInternal = (
  item: MeetingItemRepresentation | MeetingItem,
  userFullName: string,
  comment: string,
  separator?: string,
) => {
  if (!item.comments) {
    item.comments = "";
  }
  if (separator) {
    item.comments += separator + "\n";
  }
  item.comments += `[${userFullName} - ${dateTimeFormatter.format(new Date())}]: ${comment}`;
  if (separator) {
    item.comments += `\n` + separator;
  }
  item.comments += "\n";
};

export const ItemEditingPanel: FC<ItemEditingPanelProps> = (
  props: ItemEditingPanelProps,
): ReactElement => {
  const { t } = useTranslation();
  const [content, setContent] = useState(props.meetingItem.content);
  const [category, setCategory] = useState(props.meetingItem.category);
  const [prioritized, setPrioritized] = useState(props.meetingItem.prioritized);
  const [status, setStatus] = useState(props.meetingItem.status);
  const [risk, setRisk] = useState(props.meetingItem.risk);
  const [actor, setActor] = useState(props.meetingItem.actor);
  const [dueDate, setDueDate] = useState(props.meetingItem.dueDate);
  const [comment, setComment] = useState("");
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachmentsCount, setAttachmentsCount] = useState(props.meetingItem.attachments || 0);
  const [showPersonalNotesModal, setShowPersonalNotesModal] = useState(false);
  const [personalNotes, setPersonalNotes] = useState<PersonalNote[]>([]);
  const [fetchedMeeting, setFetchedMeeting] = useState<Meeting | null>(null);
  const [meetingFetchFailed, setMeetingFetchFailed] = useState(false);
  const { instance } = useMsal();
  const appContext = useContext<AppContext>(Context);

  // Resolve meeting: use prop if available, otherwise use lazily fetched data
  const resolvedMeeting = props.meeting || fetchedMeeting;

  // Sync state when dialog opens or meetingItem changes
  useEffect(() => {
    if (props.open) {
      setContent(props.meetingItem.content);
      setCategory(props.meetingItem.category);
      setPrioritized(props.meetingItem.prioritized);
      setStatus(props.meetingItem.status);
      setRisk(props.meetingItem.risk);
      setActor(props.meetingItem.actor);
      setDueDate(props.meetingItem.dueDate);
      setComment("");
      setWarningDialogOpen(false);
      setShowAttachments(false);
    } else {
      setShowAttachments(false);
      setFetchedMeeting(null);
      setMeetingFetchFailed(false);
    }
  }, [props.open, props.meetingItem]);

  // Lazily fetch meeting data when meeting prop is not provided
  useEffect(() => {
    if (props.open && !props.meeting && props.meetingItem.meetingId) {
      const meetingService = new MeetingService(
        config.api.baseUrl,
        "/meetings",
      );
      meetingService
        .get(props.meetingItem.meetingId)
        .then((meeting) => {
          if (meeting) {
            setFetchedMeeting(meeting);
            setMeetingFetchFailed(false);
          } else {
            // Axios interceptor swallows non-401 errors, resolving with undefined
            setFetchedMeeting(null);
            setMeetingFetchFailed(true);
          }
        })
        .catch(() => {
          setFetchedMeeting(null);
          setMeetingFetchFailed(true);
        });
    }
  }, [props.open, props.meeting, props.meetingItem.meetingId]);

  // Memoize userFullName to avoid recalculation
  const userFullName = React.useMemo(() => {
    return appContext.state.invoker.lastName &&
      appContext.state.invoker.firstName
      ? `${appContext.state.invoker.firstName} ${appContext.state.invoker.lastName}`
      : instance.getActiveAccount()?.name;
  }, [
    appContext.state.invoker.firstName,
    appContext.state.invoker.lastName,
    instance,
  ]);

  // Memoize permission checks
  const isCreator = React.useMemo(
    () => props.meetingItem.creator === appContext.state.invoker.email,
    [props.meetingItem.creator, appContext.state.invoker.email],
  );

  const isActor = React.useMemo(
    () => props.meetingItem.actor === appContext.state.invoker.email,
    [props.meetingItem.actor, appContext.state.invoker.email],
  );

  const isResponsible = React.useMemo(
    () => isCreator || isActor,
    [isCreator, isActor],
  );

  const editContent = React.useMemo(
    () => !props.readOnly && props.allowMetadataEditing && isResponsible,
    [props.readOnly, props.allowMetadataEditing, isResponsible],
  );

  const editCategory = React.useMemo(
    () => !props.readOnly && props.allowMetadataEditing && isResponsible,
    [props.readOnly, props.allowMetadataEditing, isResponsible],
  );

  const editActor = React.useMemo(
    () => !props.readOnly && props.allowMetadataEditing && isCreator,
    [props.readOnly, props.allowMetadataEditing, isCreator],
  );

  // Check if user has permission to access the full meeting
  const canAccessMeeting = React.useMemo(() => {
    if (!resolvedMeeting) return false;
    const userEmail = appContext.state.invoker.email?.toLowerCase();
    if (!userEmail) return false;
    const isParticipant =
      resolvedMeeting.participants?.some(
        (p) => p.toLowerCase() === userEmail,
      ) || false;
    const isViewer =
      resolvedMeeting.viewers?.some((v) => v.toLowerCase() === userEmail) ||
      false;
    const isMeetingCreator =
      resolvedMeeting.creator?.toLowerCase() === userEmail;
    return isParticipant || isViewer || isMeetingCreator;
  }, [resolvedMeeting, appContext.state.invoker.email]);

  const editDueDate = React.useMemo(
    () => !props.readOnly && (isCreator || (isActor && !prioritized)),
    [props.readOnly, isCreator, isActor, prioritized],
  );

  const editStatus = React.useMemo(
    () =>
      !props.readOnly && props.allowMetadataEditing && (isCreator || isActor),
    [props.readOnly, props.allowMetadataEditing, isCreator, isActor],
  );

  // Memoize actor options to prevent recreation
  const actorOptions = React.useMemo(
    () => [
      ...(appContext.state.selectedList?.participants || []),
      ...(appContext.state.invoker?.invitedUserEmails || []),
    ],
    [
      appContext.state.selectedList?.participants,
      appContext.state.invoker?.invitedUserEmails,
    ],
  );

  const hasChanges = React.useMemo(() => {
    return (
      props.meetingItem.content !== content ||
      props.meetingItem.category !== category ||
      props.meetingItem.dueDate !== dueDate ||
      props.meetingItem.status !== status ||
      props.meetingItem.prioritized !== prioritized ||
      props.meetingItem.actor !== actor ||
      props.meetingItem.risk !== risk
    );
  }, [
    content,
    category,
    dueDate,
    status,
    prioritized,
    actor,
    risk,
    props.meetingItem,
  ]);

  const handleWarningClose = React.useCallback(() => {
    if (hasChanges) {
      setWarningDialogOpen(true);
    } else {
      props.onClose();
    }
  }, [hasChanges, props.onClose]);

  const postComment = React.useCallback(() => {
    addCommentToItem(props.meetingItem, userFullName, comment, t);
    setComment("");
    props.onUpdate(props.meetingItem);
  }, [props.meetingItem, userFullName, comment, t, props.onUpdate]);

  const saveChanges = React.useCallback(() => {
    props.meetingItem.content = content;
    props.meetingItem.category = category;
    props.meetingItem.prioritized = prioritized;
    props.meetingItem.status = status;
    props.meetingItem.risk = risk;
    props.meetingItem.actor = actor;
    props.meetingItem.dueDate = dueDate;
    if (comment.trim()) {
      addCommentToItem(props.meetingItem, userFullName, comment, t);
      setComment("");
    }
    props.onUpdate(props.meetingItem);
    props.onClose();
  }, [
    props.meetingItem,
    content,
    category,
    prioritized,
    status,
    risk,
    actor,
    dueDate,
    comment,
    userFullName,
    t,
    props.onUpdate,
    props.onClose,
  ]);

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth="lg"
        onClose={handleWarningClose}
        aria-labelledby="draggable-dialog-title"
        open={props.open}
        keepMounted={false}
        PaperComponent={PaperComponent} slotProps={{ paper: {
          style: {
            backgroundColor: "white",
          },
        } }}
      >
        <Box
          id="draggable-dialog-title"
          sx={{ display: "flex", alignItems: "center", cursor: "grab" }}
        >
          <DialogTitle sx={{ p: 2, fontWeight: 400 }}>
            {t("item.editPanel")}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleWarningClose}
            sx={theme => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500]
            })}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: "1 1 0" }}>
              {/* item panel */}

              <Box>
                <Typography variant="button" sx={{
                  fontWeight: 600
                }}>
                  {t("item.details")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr",
                  columnGap: 2,
                  rowGap: 1,
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* creator---------------------------------------- */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.creator")}:
                </Typography>
                <Typography variant="body2">
                  {props.meetingItem.creator || t("item.notAvailable")}
                </Typography>

                {/* meeting------------------------------------------ */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.meetingName")}:
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2">
                    {props.meetingItem.meetingName || resolvedMeeting?.name}
                  </Typography>
                  {props.meetingItem.meetingId && (
                    <>
                      <MeetingInfoTooltip
                        placement="right"
                        arrow
                        title={
                          <Box sx={{ p: 2, minWidth: 280 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1.5,
                              }}
                            >
                              <EventNoteIcon fontSize="small" color="primary" />
                              <Typography variant="subtitle2" sx={{
                                fontWeight: 600
                              }}>
                                {t("meeting_info.title")}
                              </Typography>
                            </Box>
                            <Divider sx={{ mb: 1.5 }} />

                            {/* Meeting Name */}
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 600
                                }}>
                                {t("item.meetingName")}
                              </Typography>
                              <Typography variant="body2">
                                {props.meetingItem.meetingName ||
                                  resolvedMeeting?.name ||
                                  t("item.notAvailable")}
                              </Typography>
                            </Box>

                            {/* Date */}
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 600
                                }}>
                                {t("meeting_info.date")}
                              </Typography>
                              <Typography variant="body2">
                                {props.meetingItem.meetingDate ||
                                resolvedMeeting?.date
                                  ? dateFormatter.format(
                                      new Date(
                                        (props.meetingItem.meetingDate ??
                                          resolvedMeeting?.date) as Date,
                                      ),
                                    )
                                  : t("item.notAvailable")}
                              </Typography>
                            </Box>
                            {/* Participants */}
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 600
                                }}>
                                {t("meeting_info.participants")}
                              </Typography>
                              {resolvedMeeting?.participants &&
                              resolvedMeeting.participants.length > 0 ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.25,
                                  }}
                                >
                                  {resolvedMeeting.participants.map(
                                    (p, idx) => (
                                      <Typography
                                        key={idx}
                                        variant="body2"
                                        sx={{ fontSize: "0.8rem" }}
                                      >
                                        {p}
                                      </Typography>
                                    ),
                                  )}
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                    fontStyle: "italic"
                                  }}>
                                  {t("item.notAvailable")}
                                </Typography>
                              )}
                            </Box>

                            {/* Summary */}
                            <Box sx={{ mb: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 600
                                }}>
                                {t("meeting_info.summary")}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontStyle: "italic"
                                }}>
                                {t("meeting_info.no_summary_available")}
                              </Typography>
                            </Box>

                            {canAccessMeeting && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary"
                                  }}
                                >
                                  {t("meeting_info.click_to_open")}
                                </Typography>
                              </>
                            )}
                          </Box>
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canAccessMeeting || resolvedMeeting?.status !== "published"}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canAccessMeeting && resolvedMeeting?.status === "published") {
                                window.open(
                                  `/library/${props.meetingItem.meetingId}`,
                                  "_blank",
                                );
                              }
                            }}
                            sx={theme => ({
                              color: canAccessMeeting && resolvedMeeting?.status === "published"
                                ? (theme) => theme.palette.primary.main
                                : (theme) => theme.palette.action.disabled,

                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },

                              "&.Mui-disabled": {
                                color: theme.palette.action.disabled,
                              }
                            })}
                          >
                            <EventNoteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </MeetingInfoTooltip>
                      {!canAccessMeeting &&
                        (resolvedMeeting || meetingFetchFailed) && (
                          <Tooltip
                            title={t("meeting_info.no_permission")}
                            arrow
                            placement="top"
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={theme => ({
                                color: theme.palette.warning.main,
                                fontSize: "1rem",
                                cursor: "help"
                              })}
                            />
                          </Tooltip>
                        )}
                    </>
                  )}
                </Box>
                <Divider sx={{ gridColumn: "1 / -1", width: "100%", my: 1 }} />

                {/* category--------------------------------------- */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.category")}:
                </Typography>
                {editCategory ? (
                  <TextField
                    className="lighter-textfield"
                    id="category"
                    value={category}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setCategory(event.target.value)
                    }
                  />
                ) : (
                  <Typography variant="body2">
                    {props.meetingItem.category}
                  </Typography>
                )}

                {/* what------------------------------------------ */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.content")}:
                </Typography>
                {editContent ? (
                  <TextField
                    className="lighter-textfield"
                    sx={{
                      flexGrow: 1,
                      mb: 2,
                    }}
                    id="itemDescription"
                    fullWidth={true}
                    autoFocus={true}
                    required={true}
                    multiline
                    rows={3}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                  />
                ) : (
                  <Typography variant="body2">{content}</Typography>
                )}

                {/* actor------------------------------------------ */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.actor")}:
                </Typography>
                {props.meetingItem.actor ? (
                  editActor ? (
                    <FreeSingleSelect
                      onValueSelected={(value) => {
                        setActor(value);
                      }}
                      options={actorOptions}
                      value={actor}
                      disabled={!props.meetingItem.task}
                      sx={{ width: 300 }}
                    />
                  ) : (
                    <Typography variant="body2">
                      {props.meetingItem.actor}
                    </Typography>
                  )
                ) : (
                  <Typography variant="body2">
                    {t("item.notAvailable")}
                  </Typography>
                )}

                {/* duedate--------------------------- */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.dueDate")}:
                </Typography>

                {props.meetingItem.dueDate ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Tooltip
                        title={
                          !editDueDate && prioritized
                            ? t("item.tooltipfixedByCreator")
                            : ""
                        }
                        placement="top"
                      >
                        <Box>
                          <DatePicker
                            className="lighter-textfield"
                            disabled={!editDueDate}
                            slotProps={{
                              textField: {
                                size: "small",
                                error: !editDueDate && prioritized,
                              },
                            }}
                            sx={{ minWidth: 200, maxWidth: 200 }}
                            value={dayjs(dueDate)}
                            onChange={(newDate) => setDueDate(newDate.toDate())}
                          />
                        </Box>
                      </Tooltip>
                      {/* show Priority Switch for Creator only and when due date is Fixed by Creator show warning for Actor */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 200,
                        }}
                      >
                        {!editDueDate && prioritized && isActor ? (
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.75rem",
                              fontStyle: "italic",
                            }}
                          >
                            {t("item.fixedByCreator")}
                          </Typography>
                        ) : (
                          isCreator && (
                            <>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.8rem" }}
                              >
                                {t("item.fixedDueDate")}
                              </Typography>
                              <Tooltip
                                title={t("item.fixedDueDateInfo")}
                                placement="top"
                              >
                                <Switch
                                  size="medium"
                                  checked={prioritized}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) => setPrioritized(event.target.checked)}
                                  color="primary"
                                  disabled={!editDueDate}
                                />
                              </Tooltip>
                            </>
                          )
                        )}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2">
                    {t("item.notAvailable")}
                  </Typography>
                )}

                {/* status---------------------------- */}
                <Typography
                  variant="button"
                  sx={theme => ({
                    color: theme.palette.secondary.main
                  })}
                >
                  {t("item.status")}:
                </Typography>
                {props.meetingItem.task ? (
                  <Box sx={{ width: "150px" }}>
                    <ItemStatus
                      status={status}
                      lastModifiedBy={props.meetingItem.actor}
                      onChange={
                        editStatus
                          ? (newStatus) => setStatus(newStatus)
                          : undefined
                      }
                      readOnly={!editStatus}
                    />
                  </Box>
                ) : (
                  <Box>{t("item.notAvailable")}</Box>
                )}

                {/* Risk---------------------------- */}
                {props.meetingItem.task && (
                  <Typography
                    variant="button"
                    sx={theme => ({
                      color: theme.palette.secondary.main
                    })}
                  >
                    {t("item.riskScore")}:
                  </Typography>
                )}
                {props.meetingItem.task && (
                  <Box sx={{ fontSize: "0.875rem" }}>
                    <FormControl sx={{ width: 300 }}>
                      <Select
                        className="lighter-textfield"
                        disabled={
                          props.readOnly ||
                          !props.allowMetadataEditing ||
                          (!isCreator && !isActor)
                        }
                        value={risk}
                        onChange={(event: { target: { value: string } }) => {
                          {
                            setRisk(
                              enumFromStringValue(
                                RiskIndicator,
                                event.target.value,
                              ),
                            );
                          }
                        }}
                      >
                        <MenuItem value={RiskIndicator.NoRisk}>
                          {t(`item_risk.${RiskIndicator.NoRisk}`)}
                        </MenuItem>
                        <MenuItem value={RiskIndicator.OnTrack}>
                          {t(`item_risk.${RiskIndicator.OnTrack}`)}
                        </MenuItem>
                        <MenuItem value={RiskIndicator.AtRisk}>
                          {t(`item_risk.${RiskIndicator.AtRisk}`)}
                        </MenuItem>
                        <MenuItem value={RiskIndicator.Roadblock}>
                          {t(`item_risk.${RiskIndicator.Roadblock}`)}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: "1 1 0" }}>
              <Box>
                <Typography variant="button" sx={{
                  fontWeight: 600
                }}>
                  {t("item.comments")}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <TextField
                sx={theme => ({
                  "& .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: theme.palette.background.paper,
                  },

                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: theme.palette.primary.main,
                  }
                })}
                variant="outlined"
                id="itemComments"
                // label={t('item.comments').toUpperCase()}
                multiline
                fullWidth={true}
                disabled={true}
                rows={11}
                value={props.meetingItem.comments}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 2,
                  gap: 1,
                }}
              >
                <TextField
                  sx={{
                    flexGrow: 1,
                  }}
                  id="addNewItemComment"
                  label={t("item.addComment")}
                  multiline
                  fullWidth={true}
                  rows={2}
                  value={comment}
                  disabled={props.readOnly}
                  onChange={(e) => setComment(e.target.value)}
                />
                <IconButton
                  aria-label="post"
                  onClick={postComment}
                  disabled={props.readOnly || !comment.trim()}
                  sx={theme => ({
                    color: theme.palette.primary.main
                  })}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <Divider sx={{ mb: 1 }} />
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<LockOutlinedIcon />}
            onClick={() => setShowPersonalNotesModal(true)}
          >
            {t("meetingNotes.personalNotes.sectionTitle")}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={() => setShowAttachments(true)}
            disabled={
              !props.allowAttachmentsEditing &&
              (props.meetingItem.attachments || 0) === 0
            }
          >
            {t("dashboard.itemAttachments_label", {
              count: attachmentsCount,
            })}
          </Button>
          <Button
            variant="contained"
            autoFocus
            disabled={props.readOnly || !content.trim()}
            onClick={saveChanges}
          >
            {t("item.saveChanges")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Unsaved Changes Warning Dialog */}
      <ConfirmationPanel
        action="warning"
        showModal={warningDialogOpen}
        hideModal={() => setWarningDialogOpen(false)}
        confirmModal={() => {
          setWarningDialogOpen(false);
          props.onClose();
        }}
      />
      {/* Attachments Modal */}
      <AttachmentsModal
        open={showAttachments}
        allowModifications={props.allowAttachmentsEditing}
        height="70vh"
        uploadPrefix={
          props.meetingItem.meetingId
            ? `items/meetings/${props.meetingItem.meetingId}/items/${props.meetingItem.id}/`
            : `items/quicktasks/${props.meetingItem.id}/`
        }
        meetingId={props.meetingItem.meetingId}
        itemId={props.meetingItem.id}
        onClose={() => setShowAttachments(false)}
        onAttachmentsCountChanged={(
          count: number,
          meetingId?: string,
          itemId?: string,
        ) => {
          props.meetingItem.attachments = count;
          setAttachmentsCount(count);
          props.onAttachmentsCountChanged?.(count, meetingId, itemId);
        }}
      />
      {/* Personal Notes Modal */}
      <PersonalNotesModal
        open={showPersonalNotesModal}
        onClose={() => setShowPersonalNotesModal(false)}
        targetType={
          props.meetingItem.meetingId
            ? PersonalNoteTargetType.MeetingItem
            : props.meetingItem.isCoachingTask
              ? PersonalNoteTargetType.CoachingTask
              : PersonalNoteTargetType.QuickTask
        }
        targetId={props.meetingItem.id || null}
        targetItem={props.meetingItem}
        onNotesChange={(notes) => setPersonalNotes(notes)}
      />
    </>
  );
};
